/**
 * State machine engine — Option A conversation driver.
 *
 * Walks through stepDefinitions, validates input, handles skipIf
 * conditions, and produces ChatMessage objects.
 */

import STEPS from "./stepDefinitions";

let _idCounter = 0;
function msgId() {
  return `msg-${Date.now()}-${++_idCounter}`;
}

/**
 * Create the initial assistant message for a given step.
 */
export function buildStepMessage(step, config, context) {
  if (!step || step.type === "confirm") {
    return {
      id: msgId(),
      role: "assistant",
      content: "Here's a summary of your agent. Ready to create it?",
      quickReplies: [
        { label: "Create Agent", value: "__create__", icon: "\u2714" },
        { label: "Start Over", value: "__reset__", icon: "\u21BA" },
      ],
      meta: { stepId: "confirm" },
      isConfirm: true,
    };
  }

  let options = step.options || [];
  if (step.optionsFrom) {
    options = step.optionsFrom(config, context);
  }

  const quickReplies =
    step.type === "single-select" || step.type === "multi-select"
      ? options.map((o) => ({ label: o.label, value: o.value, icon: o.icon }))
      : null;

  return {
    id: msgId(),
    role: "assistant",
    content: step.question,
    quickReplies,
    meta: { stepId: step.id, type: step.type, fieldPath: step.fieldPath },
  };
}

export function buildUserMessage(text) {
  return { id: msgId(), role: "user", content: text, quickReplies: null, meta: {} };
}

export function buildErrorMessage(error) {
  return { id: msgId(), role: "assistant", content: error, quickReplies: null, meta: { isError: true } };
}

export function buildInfoMessage(text) {
  return { id: msgId(), role: "assistant", content: text, quickReplies: null, meta: { isInfo: true } };
}

/**
 * Get the display label for a value from step options.
 */
export function getDisplayLabel(step, value, config, context) {
  let options = step.options || [];
  if (step.optionsFrom) options = step.optionsFrom(config, context);
  if (step.type === "multi-select" && Array.isArray(value)) {
    if (value.length === 0) return "All (default)";
    return value.map((v) => {
      const opt = options.find((o) => o.value === v);
      return opt ? opt.label : v;
    }).join(", ");
  }
  const opt = options.find((o) => o.value === value);
  return opt ? opt.label : String(value);
}

/**
 * Resolve the default value for a step.
 */
export function resolveDefault(step, config) {
  if (typeof step.default === "function") return step.default(config);
  return step.default ?? null;
}

/**
 * Get the next step index, skipping steps whose skipIf returns true.
 */
export function getNextStepIndex(currentIndex, config) {
  let nextIdx = currentIndex + 1;
  while (nextIdx < STEPS.length) {
    const step = STEPS[nextIdx];
    if (step.skipIf && step.skipIf(config)) {
      nextIdx++;
      continue;
    }
    return nextIdx;
  }
  return STEPS.length - 1; // confirm step
}

export { STEPS };
